import { Injectable } from '@angular/core';

/**
 * StorageService (Angular 18).
 *
 * Replaces the AngularJS legacy service at:
 *   apps/b2b-ui/common/services/storage/storage.service.js
 *
 * The legacy service was a thin re-export of `localStorageService` from the
 * angular-local-storage npm package, configured with prefix `'RBAC'` in
 * apps/b2b-ui/app.ts. This native replacement preserves that prefix so keys
 * stored by either implementation are interchangeable, and `clearAll()` only
 * removes prefixed keys (matching the third-party library's behaviour).
 *
 * Exposed to AngularJS via `downgradeInjectable` under the legacy name
 * `'StorageService'` (see ng-downgraded-components.ts).
 */
@Injectable({ providedIn: 'root' })
export class StorageService {
  private readonly prefix = 'RBAC.';

  get<T>(key: string): T | null {
    const raw = localStorage.getItem(this.prefix + key);
    if (raw === null) {
      return null;
    }
    try {
      return JSON.parse(raw) as T;
    } catch {
      return raw as unknown as T;
    }
  }

  set<T>(key: string, value: T): void {
    localStorage.setItem(this.prefix + key, JSON.stringify(value));
  }

  remove(key: string): void {
    localStorage.removeItem(this.prefix + key);
  }

  clearAll(): void {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== null && key.startsWith(this.prefix)) {
        toRemove.push(key);
      }
    }
    for (const key of toRemove) {
      localStorage.removeItem(key);
    }
  }

  keys(): string[] {
    const result: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== null && key.startsWith(this.prefix)) {
        result.push(key.slice(this.prefix.length));
      }
    }
    return result;
  }
}
